import * as React from "react"
import { Check, ChevronsUpDown, Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface Project {
  id: string
  name: string
  project_number: number | string | null  // QF-01: Can be null for proposals; backend returns TEXT
  client_name?: string | null
  // v0.14 Group 9: All client names attached to project (primary first). When
  // present, ProjectCombobox uses the full list for search-string matching so
  // typing a secondary client surfaces the project, and renders a multi-client
  // label ("Primary, Secondary" or "Primary +N more").
  client_names?: string[] | null
  client_count?: number | null
}

// v0.14 Group 9: Format the client(s) label shown beside a project. Returns:
//   - `null` if there are no clients (caller hides the parenthetical)
//   - "Client" (single)
//   - "Client A, Client B" (2-3)
//   - "Client A +N more" (4+)
export function formatProjectClientLabel(project: Pick<Project, "client_name" | "client_names">): string | null {
  const names = (project.client_names ?? []).filter((n): n is string => typeof n === "string" && n.trim().length > 0)
  if (names.length === 0) {
    return project.client_name ? project.client_name : null
  }
  if (names.length === 1) {
    return names[0]
  }
  if (names.length <= 3) {
    return names.join(", ")
  }
  return `${names[0]} +${names.length - 1} more`
}

interface ProjectComboboxProps {
  projects: Project[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  onCreateNew?: () => void
}

export function ProjectCombobox({
  projects,
  value,
  onValueChange,
  placeholder = "Select project...",
  searchPlaceholder = "Search projects...",
  emptyText = "No project found.",
  disabled = false,
  className,
  onCreateNew,
}: ProjectComboboxProps) {
  const [open, setOpen] = React.useState(false)

  const selectedProject = projects.find((project) => project.id === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select project"
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {selectedProject ? (
            (() => {
              const clientLabel = formatProjectClientLabel(selectedProject)
              return (
                <span className="truncate">
                  {/* QF-01: Show "Proposal" instead of "#null" for proposals without project_number */}
                  {selectedProject.project_number ? `#${selectedProject.project_number}` : 'Proposal'} - {selectedProject.name}
                  {clientLabel && ` (${clientLabel})`}
                </span>
              )
            })()
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid="project-search-input"
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {projects.map((project) => {
                // v0.14 Group 9: Include EVERY client name in the searchable string
                // so the Command component's client-side fuzzy match surfaces the
                // project when typing any client (primary OR secondary). Falls
                // back to legacy `client_name` for callers that haven't migrated.
                const allClientNamesForSearch = (project.client_names && project.client_names.length > 0)
                  ? project.client_names.join(' ')
                  : (project.client_name || '')
                const clientLabel = formatProjectClientLabel(project)
                return (
                  <CommandItem
                    key={project.id}
                    value={`${project.project_number || 'Proposal'} ${project.name} ${allClientNamesForSearch}`}
                    onSelect={() => {
                      onValueChange(project.id)
                      setOpen(false)
                    }}
                    data-project-number={project.project_number}
                    data-client-count={project.client_count ?? undefined}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === project.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">
                      {/* QF-01: Show "Proposal" instead of "#null" for proposals without project_number */}
                      {project.project_number ? `#${project.project_number}` : 'Proposal'} - {project.name}
                      {clientLabel && (
                        <span className="text-muted-foreground ml-1">
                          ({clientLabel})
                        </span>
                      )}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {onCreateNew && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__create_new__"
                    onSelect={() => {
                      onCreateNew();
                      setOpen(false);
                    }}
                    className="text-primary font-medium"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Project
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
